import React from 'react';

interface TutorialOverlayProps {
  currentStep: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onComplete: () => void;
  canSkip: boolean;
  isFirstTime: boolean;
}

const TutorialOverlay: React.FC<TutorialOverlayProps> = ({
  currentStep,
  onNext,
  onPrevious,
  onSkip,
  onComplete,
  canSkip,
  isFirstTime,
}) => {
  const totalSteps = 4;

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="tutorial-step tutorial-welcome">
            <div className="welcome-controller-icon" aria-hidden="true">🎮</div>

            <div className="welcome-title-block">
              <span className="welcome-eyebrow">Welcome to</span>
              <h1 className="welcome-title">HISTOCRACY!</h1>
              <div className="welcome-laurel-divider" aria-hidden="true">
                <span className="laurel laurel-left">🌿</span>
                <span className="divider-line" />
                <span className="laurel laurel-right">🌿</span>
              </div>
            </div>

            <p className="tutorial-description">
              Test your history knowledge, compete globally,<br />
              and earn crypto rewards on Base blockchain.
            </p>

            <div className="welcome-feature-cards">
              <div className="welcome-card welcome-card--gold">
                <span className="welcome-card-num">1</span>
                <span className="welcome-card-icon" aria-hidden="true">🏆</span>
                <h3>Compete Globally</h3>
                <p>Climb the leaderboards and prove your history expertise.</p>
              </div>
              <div className="welcome-card welcome-card--blue">
                <span className="welcome-card-num">2</span>
                <span className="welcome-card-icon" aria-hidden="true">💎</span>
                <h3>Earn Rewards</h3>
                <p>Earn crypto rewards for correct answers and achievements.</p>
              </div>
              <div className="welcome-card welcome-card--red">
                <span className="welcome-card-num">3</span>
                <span className="welcome-card-icon" aria-hidden="true">🎯</span>
                <h3>Choose Your Tier</h3>
                <p>Start free or unlock Premium and VIP for exclusive perks.</p>
              </div>
              <div className="welcome-card welcome-card--purple">
                <span className="welcome-card-num">4</span>
                <span className="welcome-card-icon" aria-hidden="true">🪶</span>
                <h3>Sharpen Your Mind</h3>
                <p>Learn, explore, and master the greatest moments in history.</p>
              </div>
            </div>

            <div className="welcome-cta-bar">
              <span className="welcome-cta-icon" aria-hidden="true">📜</span>
              <div className="welcome-cta-text">
                <strong>Ready to make history?</strong>
                <span>Let's learn the basics in just 4 quick steps!</span>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="tutorial-step tutorial-gameplay">
            <div className="tutorial-icon">❤️</div>
            <h2 className="tutorial-title">How to Play</h2>
            <p className="tutorial-description">
              Answer questions before time runs out and manage your hearts!
            </p>

            <div className="tutorial-gameplay-grid">
              <div className="gameplay-section">
                <h3>⏱️ Timer</h3>
                <p>You have <strong>5 seconds</strong> per question</p>
                <p className="detail-text">Answer quickly or lose a heart!</p>
              </div>

              <div className="gameplay-section">
                <h3>❤️ Hearts</h3>
                <div className="hearts-display">
                  <span className="heart">❤️</span>
                  <span className="heart">❤️</span>
                  <span className="heart">❤️</span>
                </div>
                <p className="detail-text">Start with 3, max 5 hearts</p>
              </div>

              <div className="gameplay-section">
                <h3>🔥 Streaks</h3>
                <p>Get <strong>3 correct</strong> in a row</p>
                <p className="detail-text">Earn a bonus heart!</p>
              </div>

              <div className="gameplay-section">
                <h3>🎯 Tiers</h3>
                <p><strong>Free:</strong> 30 questions</p>
                <p><strong>Premium/VIP:</strong> More rewards!</p>
              </div>
            </div>

            <p className="tutorial-note">
              💡 Click an answer to lock it in - no confirmation needed!
            </p>
          </div>
        );

      case 2:
        return (
          <div className="tutorial-step tutorial-rewards-challenges">
            <div className="tutorial-icon">🚀</div>
            <h2 className="tutorial-title">Challenges & Rewards</h2>
            <p className="tutorial-description">
              Hard mode questions and crypto earnings on Base blockchain!
            </p>

            <div className="tutorial-split-sections">
              <div className="section-left">
                <h3>🔥 Hard Mode</h3>
                <p className="section-desc">Some questions include special challenges:</p>
                <div className="challenge-types-compact">
                  <div className="challenge-type">⚡ Moving buttons</div>
                  <div className="challenge-type">🔄 Mirrored text</div>
                  <div className="challenge-type">👻 Fading elements</div>
                </div>
                <p className="section-note">Tests your knowledge AND adaptability!</p>
              </div>

              <div className="section-right">
                <h3>💰 Crypto Rewards</h3>
                <p className="section-desc">Earn real rewards:</p>
                <div className="reward-tiers-compact">
                  <div className="reward-tier">
                    <span className="tier-label">Free</span>
                    <span className="tier-reward">1x rewards</span>
                  </div>
                  <div className="reward-tier">
                    <span className="tier-label">Premium</span>
                    <span className="tier-reward">2x rewards</span>
                  </div>
                  <div className="reward-tier">
                    <span className="tier-label">VIP</span>
                    <span className="tier-reward">3x rewards</span>
                  </div>
                </div>
                <p className="section-note">Secure on Base blockchain!</p>
              </div>
            </div>

            <div className="leaderboard-preview-compact">
              <h3>🏆 Global Leaderboards</h3>
              <p>Compete daily, weekly, and all-time to prove you're the ultimate history expert!</p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="tutorial-step tutorial-complete">
            <div className="tutorial-icon">🎉</div>
            <h1 className="tutorial-title">You're Ready!</h1>
            <p className="tutorial-description">
              Everything you need to start your Histocracy journey!
            </p>
            <div className="tutorial-recap">
              <div className="recap-item">
                <span className="recap-icon">✓</span>
                <span>Answer within 5 seconds per question</span>
              </div>
              <div className="recap-item">
                <span className="recap-icon">✓</span>
                <span>Manage hearts wisely - wrong answers and timeouts cost hearts</span>
              </div>
              <div className="recap-item">
                <span className="recap-icon">✓</span>
                <span>Build 3-answer streaks for bonus hearts</span>
              </div>
              <div className="recap-item">
                <span className="recap-icon">✓</span>
                <span>Face challenge mode for harder questions</span>
              </div>
              <div className="recap-item">
                <span className="recap-icon">✓</span>
                <span>Earn crypto rewards and climb leaderboards</span>
              </div>
            </div>
            <div className="tutorial-encouragement">
              <p className="encouragement-text">Ready to make history?</p>
              <p className="encouragement-subtext">
                Connect your wallet and select a tier to start playing!
              </p>
              <p className="tutorial-access-note">
                💡 You can access this tutorial anytime from the menu bar
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="tutorial-overlay">
      <div className="tutorial-backdrop" onClick={onSkip} />
      <div className="tutorial-content-wrapper">
        <div className="tutorial-content">
          {/* Animated 3D globe overlaying the painted horizon in the bg image */}
          <div className="modal-globe" aria-hidden="true">
            <div className="modal-globe-sphere" />
          </div>
          <button
            className="tutorial-close-btn"
            onClick={onSkip}
            aria-label="Close tutorial"
          >
            &#10005;
          </button>
          <div className="tutorial-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
              />
            </div>
            <div className="progress-text">
              Step {currentStep + 1} of {totalSteps}
            </div>
          </div>

          {renderStepContent()}

          <div className="tutorial-navigation">
            {currentStep > 0 && (
              <button
                onClick={onPrevious}
                className="tutorial-btn tutorial-btn-secondary"
              >
                ← Previous
              </button>
            )}
            
            <div className="tutorial-nav-right">
              {canSkip && currentStep > 0 && (
                <button
                  onClick={onSkip}
                  className="tutorial-btn tutorial-btn-skip"
                >
                  Skip Tutorial
                </button>
              )}
              
              {currentStep < totalSteps - 1 ? (
                <button
                  onClick={onNext}
                  className="tutorial-btn tutorial-btn-primary shimmer-animation"
                >
                  Next →
                </button>
              ) : (
                <button
                  onClick={onComplete}
                  className="tutorial-btn tutorial-btn-complete shimmer-animation"
                >
                  Start Playing! 🎮
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialOverlay;
